import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { reduxRootTypes } from 'src/types';
import { logsService, userService } from 'src/app/services';
import { actionColors, EventLogsType, LogsData } from './logs.config';
import './logs.scss';
import moment from 'moment';
import CustomTable from '../../../../components/CustomTable/CustomTable';
import { FieldsEnum } from 'src/enums/FieldsEnum';
import { stringReducer } from 'src/app/helpers/tableFilter';
import { userOfEventService } from 'src/app/services';
import { LogableFieldsPermissionMap } from './logs.config';

import { useTranslation } from 'react-i18next';
import { translateFieldsInLogsText } from 'src/app/helpers/translateFieldsInLogsText.helper';
import { reverseLookupByPath } from './helper/resolveFieldLabel';
import { SECOND } from 'src/app/constants/time.constant';
import Popover from 'antd/es/popover';
import Tooltip from 'antd/es/tooltip';
import { userActions } from 'src/app/redux/actions';

const EventLogs: React.FC<EventLogsType> = ({ activeKey }) => {
  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();

  const { fieldsList } = useSelector((state: reduxRootTypes.RootStateType) => state.fieldsState);
  const { eventList } = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);
  const { events } = useSelector((state: reduxRootTypes.RootStateType) => state.userState);
  const { alldatabasepermissions } = useSelector(
    (state: reduxRootTypes.RootStateType) => state.userRolePermissionState,
  );
  const { usersList } = useSelector((state: reduxRootTypes.RootStateType) => state.userListState);

  const [logsData, setLogsData] = useState<LogsData[]>([]);
  const [currentUsersList, setCurrentUsersList] = useState<(string | null)[]>([]);
  const [currentFieldsList, setCurrentFieldsList] = useState<string[]>([]);
  const [allFieldsList, setAllFieldsList] = useState<string[]>([]);
  const [currentEventlist, setCurrentEventList] = useState<string[]>(events.map((e) => e.id));
  const [currentTimestampRange, setCurrentTimestampRange] = useState<moment.Moment[] | null>(null);
  const [currentSortOrder, setCurrentSortOrder] = useState<string>('DESC');
  const [isEventFilterSelected, setIsEventFilterSelected] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [numberOfTotalLogs, setNumberOfTotalLogs] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    if (usersList.length) return;

    setIsLoading(true);
    const getUserListData = async () => {
      try {
        const userListData = await userService.lookupUsersTable({ blockSpinner: true });
        dispatch(userActions.setListInformation(userListData.data.rows));
        setIsLoading(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    };

    getUserListData();
  }, []);

  useEffect(() => {
    if (selectedUsers.length > 0) {
      setCurrentUsersList(selectedUsers);
    }
  }, [activeKey]);

  // const getLocationByFlowChartType = (flowChartType: FieldsEnum) => {
  //   for (const [location, types] of Object.entries(fieldTypeToLocationMap)) {
  //     if (types.includes(flowChartType)) {
  //       return location;
  //     }
  //   }
  //   return 'Unknown';
  // };

  const fieldsListRef = useRef(fieldsList);

  useEffect(() => {
    fieldsListRef.current = fieldsList;
  }, [fieldsList]);

  const getLogsData = async (currentPage: number, pageSize: number) => {
    const fieldsEnumToId = (fieldName: string): number | undefined => {
      const enumKey = fieldName.replace(/ /g, '_');

      const translatedKey = reverseLookupByPath(enumKey, 'administration.logs.fieldsEnum');

      return FieldsEnum[translatedKey as keyof typeof FieldsEnum];
    };

    const fieldsIdsArray: string[] = currentFieldsList
      .map((field) => fieldsEnumToId(field))
      .filter((id) => id !== undefined)
      .map((id) => (id ? id.toString() : ''));

    try {
      const timestampRangeStrings = currentTimestampRange?.map((momentObj) => momentObj.toISOString());

      const userIdsArray = [
        ...(currentUsersList.length > 0
          ? usersList.filter((e) => currentUsersList.includes(e.userName)).map((e) => e.id)
          : []),
        ...(currentUsersList.includes(null) ? ['null'] : []),
      ];

      const logData = await logsService.lookupLogs({
        blockSpinner: true,
        limit: pageSize.toString(),
        offset: ((currentPage - 1) * pageSize).toString(),
        userIdsArray,
        eventIdsArray: currentEventlist,
        areNullEventsIncluded: !isEventFilterSelected,
        fieldsIdsArray: Array.from(new Set(fieldsIdsArray)),
        timestampRange: timestampRangeStrings,
        sortBy: 'createdAt',
        sortOrder: currentSortOrder as 'ASC' | 'DESC',
      });

      const usersOfEvent = await userOfEventService.lookupUsersOfEvent({
        blockSpinner: true,
        eventIdsArray: currentEventlist,
      });

      const usersOfEventIds = usersOfEvent.data.rows.map((e) => e.userId);

      const eventUsers = usersList.filter((e) => usersOfEventIds.includes(e.id)).map((e) => e.userName);

      const eventUsersWithNull = [...eventUsers, null];

      setCurrentUsersList(eventUsersWithNull);

      setNumberOfTotalLogs(logData.data.count);

      const tempLog: LogsData[] = [];

      setNumberOfTotalLogs(logData.data.count);

      logData.data.rows.forEach((obj, index) => {
        const fieldType = fieldsListRef.current.find((e) => e.id === obj.fieldId)?.fieldsType;

        const event = eventList.find((e) => e.id === obj.eventId)?.eventId || t('general.notAvailable');
        // const location = getLocationByFlowChartType(fieldType as FieldsEnum);

        const detail = obj.description;
        const action = obj.description.includes('create')
          ? 'CREATE'
          : obj.description.includes('update')
          ? 'UPDATE'
          : obj.description.includes('delete')
          ? 'DELETE'
          : 'UPDATE';

        // Now, we don't accept fieldType 15 and 16 (which are ROAD TO CURRENT SETTINGS and SETTING) in the logs
        if (fieldType !== FieldsEnum.ROAD_TO_CURRENT_SETTINGS && fieldType !== FieldsEnum.SETTING) {
          const field = t(`administration.logs.fieldsEnum.${fieldType && FieldsEnum[fieldType]}`).replaceAll('_', ' ');

          const username = usersList.find((e) => e.id === obj.createdById)?.userName || 'null';

          tempLog.push({
            key: index,
            user: username,
            event: event || '',
            action,
            field,
            details: detail,
            timestamp: moment(obj.createdAt).format('HH:mm DD/MM/YYYY'),
            originalTimestamp: moment(obj.createdAt),
          });
        }
      });

      setLogsData(tempLog);

      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (usersList.length && currentFieldsList.length > 0 && activeKey === '6') {
      setIsLoading(true);
      getLogsData(currentPage, pageSize);

      const intervalId = setInterval(() => {
        getLogsData(currentPage, pageSize);
      }, 10 * SECOND);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [currentPage, pageSize, currentEventlist, currentFieldsList, activeKey, usersList]);

  const getCurrentFieldsList = () => {
    const userReadPermissions = alldatabasepermissions
      .map((permission) => permission.toString())
      .filter((permission) => permission.endsWith('1'));

    const logableFieldsPermissionsSet = new Set<string>(
      Object.values(LogableFieldsPermissionMap).flatMap((value) => value.map((num) => num.toString())),
    );

    const filteredPermissions = userReadPermissions.filter((permission) => logableFieldsPermissionsSet.has(permission));

    const filteredPermissionsName = filteredPermissions.map((permission) =>
      Object.keys(LogableFieldsPermissionMap).find((key) =>
        LogableFieldsPermissionMap[key].includes(parseInt(permission)),
      ),
    );

    setCurrentFieldsList(
      filteredPermissionsName.map((e) => t(`administration.logs.fieldsEnum.${e}`).replaceAll('_', ' ') || 'Unknown'),
    );

    setAllFieldsList(
      filteredPermissionsName.map((e) => t(`administration.logs.fieldsEnum.${e}`).replaceAll('_', ' ') || 'Unknown'),
    );
  };

  useEffect(() => {
    getCurrentFieldsList();
  }, [alldatabasepermissions, i18n.language]);

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleTableChange = (filters: any, sorter: any, pagination: any) => {
    const order = sorter.order === 'ascend' ? 'ASC' : 'DESC';

    setCurrentSortOrder(order);

    if (filters.event) {
      setIsEventFilterSelected(true);

      const selectedEventIdsName = filters.event as string[];
      const selectedEventIds = selectedEventIdsName.map((e) => events.find((event) => event.eventId === e)?.id || '');

      setCurrentEventList(selectedEventIds);
    } else {
      setIsEventFilterSelected(false);
      setCurrentEventList(events.map((e) => e.id));
    }
    if (filters.user) {
      const selectedUsers = filters.user as string[];
      setCurrentUsersList(selectedUsers);
      setSelectedUsers(selectedUsers);
    } else {
      setCurrentUsersList([]);
      setSelectedUsers([]);
    }
    if (filters.field) {
      const selectedFields = filters.field as string[];

      setCurrentFieldsList(selectedFields);
    } else {
      getCurrentFieldsList();
    }
    const { timestamp } = filters;

    let timestampRange = null;

    if (timestamp) {
      const timestampValue = timestamp[0];
      if (timestampValue === 'today') {
        timestampRange = [moment().startOf('day'), moment().endOf('day')];
      } else if (timestampValue === 'yesterday') {
        timestampRange = [moment().subtract(1, 'days').startOf('day'), moment().subtract(1, 'days').endOf('day')];
      } else if (timestampValue === 'last7days') {
        timestampRange = [moment().subtract(7, 'days').startOf('day'), moment().endOf('day')];
      } else if (timestampValue === 'lastmonth') {
        timestampRange = [
          moment().subtract(1, 'months').startOf('month'),
          moment().subtract(1, 'months').endOf('month'),
        ];
      } else if (timestampValue === 'last3months') {
        timestampRange = [moment().subtract(3, 'months').startOf('month'), moment().endOf('month')];
      } else if (timestampValue === 'last6months') {
        timestampRange = [moment().subtract(6, 'months').startOf('month'), moment().endOf('month')];
      }
    }

    setCurrentTimestampRange(timestampRange);
  };

  const columns = [
    {
      title: t('general.event').toLocaleUpperCase(),
      dataIndex: 'event',
      key: 'event',
      filters: stringReducer(events.map((e) => ({ text: e.eventId, value: e.eventId }))),
      onFilter: (value: string, record: LogsData) => record.event.indexOf(value) === 0,
      width: '11%',
    },
    {
      title: t('administration.logs.user').toLocaleUpperCase(),
      dataIndex: 'user',
      key: 'user',
      onFilter: (value: string, record: LogsData) => record.user.indexOf(value) === 0,
      filters: stringReducer([
        ...currentUsersList.map((e) => ({ text: e, value: e })),
        { text: <span style={{ color: 'gray' }}>{t('general.deletedUser')}</span>, value: null },
      ]),
      width: '10%',
      render: (text: string) => (
        <span style={{ color: text === 'null' ? '#666' : 'inherit' }}>
          {text === 'null' ? t('general.deletedUser') : text}
        </span>
      ),
    },
    {
      title: t('administration.logs.action').toLocaleUpperCase(),
      dataIndex: 'action',
      key: 'action',
      render: (text: keyof typeof actionColors) => (
        <span style={{ color: actionColors[text] || actionColors.Unknown }}>{t(`administration.logs.${text}`)}</span>
      ),
      width: '10%',
    },
    {
      title: t('administration.logs.field').toLocaleUpperCase(),
      dataIndex: 'field',
      key: 'field',
      filters: stringReducer(allFieldsList.map((e) => ({ text: e, value: e }))),
      onFilter: (value: string, record: LogsData) => record.field.indexOf(value) === 0,
      render: (text: string) => {
        const truncatedText = text.length > 20 ? `${text.slice(0, 20)}...` : text;
        return (
          <Popover content={text}>
            <span>{truncatedText}</span>
          </Popover>
        );
      },
      width: '20%',
    },
    {
      title: t('administration.logs.details').toLocaleUpperCase(),
      dataIndex: 'details',
      key: 'details',
      width: '40%',
      render: (text: string) => {
        const isJsonString = (str: string) => {
          try {
            JSON.parse(str);
            return true;
          } catch (e) {
            return false;
          }
        };

        if (isJsonString(text)) {
          const json = JSON.parse(text);

          const generateMessage = (data: any) => {
            let messages = [];
            for (let key in data) {
              const { field, from, to } = data[key];
              if (field !== undefined && from !== undefined && to !== undefined) {
                const message = (
                  <div key={key}>
                    <span
                      style={{
                        fontWeight: 'bold',
                      }}
                    >
                      {field}:{' '}
                    </span>
                    <span style={{ color: 'gray', textDecoration: 'line-through' }}>{from}</span>
                    <span>
                      {' '}
                      --{'>'} {to}
                    </span>
                  </div>
                );
                messages.push(message);
              }
            }

            if (messages.length === 0) {
              return <div>User has been updated the form but no changes were made between old and new values.</div>;
            }

            return messages;
          };

          const fullText = `${json.realLifeSupportId} has been updated with the following changes:`;
          const visibleText = fullText.length > 50 ? `${fullText.slice(0, 50)}...` : fullText;

          return (
            <Popover
              content={
                <div>
                  <div style={{ marginBottom: 6 }}>
                    <span>
                      <span
                        style={{
                          fontWeight: 'bold',
                        }}
                      >
                        {json.realLifeSupportId}
                      </span>{' '}
                      has been updated with the following changes:
                    </span>
                  </div>
                  {generateMessage(json)}
                </div>
              }
            >
              <span>{visibleText}</span>
            </Popover>
          );
        } else {
          const translatedText = translateFieldsInLogsText(text);

          const visibleText = translatedText.length > 50 ? `${translatedText.slice(0, 50)}...` : translatedText;
          return (
            <Popover content={<div>{translatedText}</div>}>
              <span>{visibleText}</span>
            </Popover>
          );
        }
      },
    },

    // {
    //   title: t('administration.logs.location').toLocaleUpperCase(),
    //   dataIndex: 'location',
    //   key: 'location',
    //   width: '10%',
    // },
    {
      title: t('administration.logs.timestamp').toLocaleUpperCase(),
      dataIndex: 'timestamp',
      key: 'timestamp',
      multiple: false,
      width: '15%',
      sorter: (a: LogsData, b: LogsData) => moment(a.originalTimestamp).unix() - moment(b.originalTimestamp).unix(),
      filters: [
        {
          text: t('general.today'),
          value: 'today',
        },
        {
          text: t('general.yesterday'),
          value: 'yesterday',
        },
        {
          text: t('general.last7days'),
          value: 'last7days',
        },
        {
          text: t('general.lastMonth'),
          value: 'lastmonth',
        },
        {
          text: t('general.last3months'),
          value: 'last3months',
        },
        {
          text: t('general.last6months'),
          value: 'last6months',
        },
      ],
      onFilter: (value: string, record: LogsData) => {
        const recordTime = moment(record.originalTimestamp);

        if (value === 'today') {
          return recordTime.isSame(moment(), 'day');
        } else if (value === 'yesterday') {
          return recordTime.isSame(moment().subtract(1, 'days'), 'day');
        } else if (value === 'last7days') {
          return recordTime.isAfter(moment().subtract(7, 'days').startOf('day'));
        } else if (value === 'lastmonth') {
          return recordTime.isBetween(
            moment().subtract(1, 'months').startOf('month'),
            moment().subtract(1, 'months').endOf('month'),
          );
        } else if (value === 'last3months') {
          return recordTime.isAfter(moment().subtract(3, 'months').startOf('month'));
        } else if (value === 'last6months') {
          return recordTime.isAfter(moment().subtract(6, 'months').startOf('month'));
        }
        return false;
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    return {
      ...col,
      onCell: (record: LogsData) => ({
        record,
        inputType: col.dataIndex === 'type' ? 'number' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
      }),
      title: (
        <Tooltip title={col.title}>
          <span className="tableTitleStyle">{col.title}</span>
        </Tooltip>
      ),
    };
  });

  return (
    <div className="event-logs">
      <CustomTable
        id="eventLogs"
        columns={mergedColumns}
        dataSource={logsData}
        numberOfTotalPage={numberOfTotalLogs}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onChange={handleTableChange}
        loading={isLoading}
        scroll={{ y: '70vh' }}
      />
    </div>
  );
};

export default EventLogs;
